import 'vuetify/styles';

import { createVuetify } from 'vuetify';
import {
  VBtn, VCard, VTabs, VTreeview,
} from 'vuetify/components';
import {
  en, fr, de, es, it, pl, zhHans,
} from 'vuetify/locale';
import { useMediaQuery } from '@vueuse/core';
import { mdi } from 'vuetify/iconsets/mdi';
import { aliases, ms } from '~/components/common/icon-sets/materialSymbols';
import { i18n } from './i18n';
import msOutlined from '~/components/common/icon-sets/materialSymbolsOutlined';

const vuetifyOptions = {
  theme: {
    defaultTheme: 'dark',
    themes: {
      dark: {
        dark: true,
        colors: {
          primary: '#EB212E', // variable vuetify theme see useTheme
          background: '#343a40', // variable vuetify theme see useTheme - bg pages
          surface: '#2a2e33', // variable vuetify theme see useTheme - bg all components
          error: '#e84b56',
          color_a: '#353A40',
          color_b: '#1a1c1e',
          color_c: '#2a2e33',
          color_d: '#313539',
          color_e: '#3f464c',
          color_f: '#63676b',
          color_g: '#a7a8a9',
          color_h: '#e0e0e0',
          color_i: '#32363B',
          hover_menus: '#382a2f',
          on_click_menu: '#7b242b',
          color_production: '#4b5e6d',
          color_production_lighten: '#708DA3',
          color_planned_stops: '#1a1c1e',
          color_unplanned_stops: '#ffff00',
          color_setups_and_adjustments: '#20abba',
          color_breakdowns: '#e84b56',
          color_minor_stops_and_idling: '#f7931e',
          color_non_quality: '#ab47bc',
          color_undefined: '#E0E0E0',
          color_good_state: '#00f3ff', // mettre la même que changeover ?
          orange: '#f15a24',
          yellow: '#FFC107',
          duration_cycle: '#8197A8', // avoir un helper ou composable pour les couleurs
          average_duration_cycle: '#FFFFFF', // voir pour utiliser white ou avoir un helper ou composable pour les couleurs
          signature_duration: '#C37BF9', // avoir un helper ou composable pour les couleurs
          black: '#000',
          white: '#fff',
          info: '#29abe2',
          color_favorite_on: '#FFC107',
          color_favorite_off: '#A7A8A9',
          warning_message: '#f7931e',

        },
      },
    },
    options: {
      customProperties: true,
    },
  },
  lang: {
    t: (key, ...params) => i18n.t(key, params),
    current: i18n.locale,
  },
};

const globalComponentStyle = {
  aliases: {
    DefaultBtn: VBtn,
    FormBtn: VBtn,
    SettingsCard: VCard,
    SectionCard: VCard,
    SettingsTabs: VTabs,
  },
  defaults: {
    DefaultBtn: {
      color: 'color_b',
      class: ['elevation-0'],
    },
    FormBtn: {
      color: 'color_b',
      class: ['elevation-0'],
    },
    SettingsCard: {
      color: 'color_c',
      elevation: '0',
      VCardActions: {
        class: ['py-4', 'px-6', 'justify-end', 'ga-4', 'border-t'],
      },
      VCardItem: {
        class: ['border-b', 'py-4'],
      },
      VCardText: {
        class: ['pt-4'],
      },
      VCardTitle: {
        class: ['text-title-medium'],
      },
    },
    SectionCard: {
      color: 'color_d',
      elevation: 1,
      rounded: 'lg',
      VCardItem: {
        class: ['border-b', 'px-6', 'pt-3', 'pb-3'],
      },
      VCardText: {
        class: ['text-body-medium', 'text-color_h', 'pa-6'],
      },
      VCardTitle: {
        class: ['text-title-medium'],
      },
    },
    SettingsTabs: {
      class: ['settings-tabs'],
      selectedClass: 'text-white',
      VTab: {
        baseColor: 'color_g',
        class: ['text-body-large'],
      },
    },
    VAlert: {
      variant: 'outlined',
    },
    VAutocomplete: {
      variant: 'underlined',
    },
    VBadge: {
      color: 'primary',
    },
    VBreadcrumbs: {
      class: ['pa-0', 'ma-0'],
      VBreadcrumbsItem: {
        class: ['text-body-small'],
      },
    },
    VBtn: {
      class: ['elevation-0'],
    },
    VCard: {
      class: ['rounded-0'],
      elevation: '0',
      VCardText: {
        class: ['text-body-large'],
      },
    },
    VCarousel: {
      color: 'white',
      VBtn: {
        variant: 'text',
      },
    },
    VCheckbox: {
      hideDetails: 'auto',
    },
    VChip: {
      color: 'color_f',
      class: ['text-label-large'],
      label: true,
      variant: 'flat',
    },
    VCombobox: {
      variant: 'underlined',
    },
    // VDateInput resolves its own text field props, so it does not inherit the VTextField
    // defaults below: the shared appearance is repeated here.
    VDateInput: {
      clearIcon: 'close',
      persistentClear: true,
      variant: 'underlined',
    },
    VDatePicker: {
      class: ['rounded-0'],
      color: 'white',
      hideHeader: true,
      title: '',
      width: '100%',
      showWeek: false,
      style: {
        maxWidth: '328px',
      },
    },
    VDataTable: {
      mobileBreakpoint: 'sm',
    },
    VField: {
      variant: 'underlined',
    },
    VInput: {
      clearIcon: 'close',
      persistentClear: true,
      variant: 'underlined',
    },
    VList: {
      class: ['rounded-0', 'elevation-0', 'pa-0'],
      bgColor: 'color_b',
      VListGroup: {
        collapseIcon: 'keyboard_arrow_up',
        color: 'white',
        expandIcon: 'keyboard_arrow_down',
      },
    },
    VNavigationDrawer: {
      VList: {
        class: ['pa-4'],
        VListItem: {
          class: ['rounded'],
        },
        VListGroup: {
          VListItem: {
            activeClass: 'v-list-item__opened',
            class: ['mt-1', 'rounded'],
            VListItemTitle: {
              class: ['text--line-height-normal', 'font-weight-medium', 'text-title-small'],
            },
          },
        },
      },
    },
    VMenu: {
      VList: {
        class: ['rounded-lg', 'elevation-1'],
        bgColor: 'color_c',
        VListItem: {
          class: ['py-0', 'text-body-medium'],
          style: {
            'min-height': '40px',
          },
          VListItemSubtitle: {
            class: ['text-body-medium'],
          },
        },
      },
    },
    VProgressLinear: {
      class: 'rounded-0',
      bgColor: 'color_d',
      bgOpacity: 1,
    },
    VRangeSlider: {
      thumbColor: 'white',
      trackColor: 'white',
      trackFillColor: 'white',
    },
    VSelect: {
      variant: 'underlined',
      VChip: {
        size: 'small',
      },
    },
    VSnackbar: {
      tile: true,
    },
    VStepper: {
      hideActions: true,
      class: ['elevation-0'],
      tile: true,
      VStepperHeader: {
        class: ['elevation-0'],
        VStepperItem: {
          color: 'white',
          errorIcon: 'error no-fill',
        },
      },
      VStepperWindow: {
        class: ['ma-0'],
      },
    },
    VSwitch: {
      baseColor: 'color_f',
      color: 'white',
    },
    VTabs: {
      bgColor: 'color_d',
      class: ['kp-v-tabs'],
      nextIcon: 'chevron_right',
      prevIcon: 'chevron_left',
      selectedClass: 'text-white bg-color_c',
      VIcon: {
        size: 'small',
      },
      VTab: {
        class: ['text-color_f', 'text-title-medium'],
      },
    },
    VTextarea: {
      variant: 'outlined',
      noResize: true,
      clearIcon: 'close',
      persistentClear: true,
    },
    VTextField: {
      clearIcon: 'close',
      persistentClear: true,
      variant: 'underlined',
    },
    VTimeline: {
      dotColor: 'white',
      fillDot: true,
      size: 'x-small',
    },
    VTooltip: {
      closeDelay: 0,
      openDelay: 700,
      disabled: useMediaQuery('(hover: none)'), // used to disable tooltips on touch devices
    },
    VTreeview: {
      collapseIcon: 'keyboard_arrow_up',
      expandIcon: 'keyboard_arrow_down',
      falseIcon: 'check_box_outline_blank',
      indeterminateIcon: 'indeterminate_check_box',
      trueIcon: 'check_box',
    },
    VWindow: {
      color: 'white',
      VBtn: {
        variant: 'text',
      },
    },
  },
};

const globalConfig = {
  ...vuetifyOptions,
  locale: {
    locale: 'fr',
    fallback: 'fr',
    messages: {
      de,
      en,
      fr,
      es,
      it,
      pl,
      zh: zhHans,
    },
  },
  icons: {
    defaultSet: 'ms',
    aliases,
    sets: {
      ms,
      msOutlined,
      mdi,
    },
  },
  components: { VTreeview },
  display: {
    mobileBreakpoint: 'md',
    // Vuetify 3 breakpoints restored (KP-19538 S-0002) - Vuetify 4 reduces the default thresholds
    thresholds: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
      xxl: 2560,
    },
  },
  ...globalComponentStyle,
};

export default createVuetify(globalConfig);
